import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AppState } from '@store/state';
import { StepperBarStepsEnum } from '@enums/stepper-bar-steps.enum';
import { StepperBarStep } from '@shared/services/extras/_interfaces_/extras-steps.interface';
import {
  Passenger,
  PassengerUpdateResponse,
  PriceSummary,
} from '@shared/services/passenger-change/_interfaces_/passenger-change.interface';
import {
  selectSelectedOrderDossierNumber,
  selectSelectedOrderFlightDetailsIdentifier,
} from '@store/selected-order/selected-order.selectors';
import * as fullscreenLoaderActions from '@store/fullscreen-loader/fullscreen-loader.actions';
import { UrlPathnameEnum } from '@enums/url-pathname.enum';
import { AppRoutesConstant } from '@constants/app-routes.costant';
import { GenderEnum } from '@enums/gender.enum';
import { PaxTypeEnum } from '@enums/pax-type.enum';
import { ResolversEnum } from '@enums/resolvers.enum';
import { FullScreenLoaderEnum } from '@enums/fullscreen-loader.enum';
import { DateFormatEnum } from '@enums/date-format.enum';
import { PaxChangeService } from '@shared/services/passenger-change/passenger-change.service';
import { ErrorModalService } from '@shared/components/error-modal/error-modal.service';
import * as setSnackBarActions from '@store/snack-bar/snack-bar.actions';
import { selectErrorModal } from '@store/error-modal/error-modal.selectors';
import { selectCommonOrderReference } from '@store/common-order/common-order.selectors';

@Component({
  selector: 'passenger-change-overview-component',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
})
export class PassengerChangeOverviewComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();
  dateFormatEnum = DateFormatEnum;
  dossierNumber: string;
  orderReference: string;
  flightIdentifier: string;
  stepperBarStepsEnum = StepperBarStepsEnum;
  stepperBarSteps: StepperBarStep[] = [];

  originalPassengers: Passenger[] = [];
  updatedPassengers: Passenger[] = [];
  priceSummary: PriceSummary;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private paxChangeService: PaxChangeService,
    private errorModalService: ErrorModalService
  ) {}

  ngOnInit(): void {
    this.setStepperBarSteps();

    this.translate.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.setStepperBarSteps();
    });

    this.store
      .select(selectSelectedOrderFlightDetailsIdentifier)
      .pipe(take(1))
      .subscribe((id) => (this.flightIdentifier = id || ''));
    this.store
      .select(selectCommonOrderReference)
      .pipe(take(1))
      .subscribe((orderReference) => (this.orderReference = orderReference || ''));

    this.loadResolvedData();
  }

  private loadResolvedData(): void {
    const resolvedData = this.route.snapshot.data[ResolversEnum.PASSENGER_UPDATE] as PassengerUpdateResponse;
    if (resolvedData) {
      this.flightIdentifier = resolvedData.identifier;

      const changed = resolvedData.changedPassengers || [];
      const originalAll = resolvedData.retrievedFlight?.passengers || [];

      this.updatedPassengers = changed;
      this.originalPassengers = changed.map(
        (changedPassenger: Passenger) =>
          originalAll.find((original: Passenger) => original.identifier === changedPassenger.identifier) || ({} as Passenger)
      );
      this.priceSummary = resolvedData.price;
    }
  }

  private setStepperBarSteps(): void {
    this.stepperBarSteps = [
      { stepName: this.translate.instant('STEPBAR_PASSENGER_CHANGE.STEP_1') },
      { stepName: this.translate.instant('STEPBAR_PASSENGER_CHANGE.STEP_2') },
    ];
  }

  getPassengerTypeLabel(): string {
    const typeMap = new Map<string, number>();

    this.updatedPassengers.forEach((p) => {
      const type = p.passengerType;
      if (type) {
        typeMap.set(type, (typeMap.get(type) || 0) + 1);
      }
    });

    const labels: string[] = [];

    if (typeMap.get(PaxTypeEnum.Adult)) {
      labels.push(`${typeMap.get(PaxTypeEnum.Adult)}x ${this.translate.instant(PaxTypeEnum.Adult).toLowerCase()}`);
    }
    if (typeMap.get(PaxTypeEnum.Child)) {
      labels.push(`${typeMap.get(PaxTypeEnum.Child)}x ${this.translate.instant(PaxTypeEnum.Child).toLowerCase()}`);
    }
    if (typeMap.get(PaxTypeEnum.Infant)) {
      labels.push(`${typeMap.get(PaxTypeEnum.Infant)}x ${this.translate.instant(PaxTypeEnum.Infant).toLowerCase()}`);
    }

    return labels.join(', ');
  }

  getTitle(gender: GenderEnum | string): string {
    return gender === GenderEnum.Female ? this.translate.instant('PASSENGER_CHANGE.MS') : this.translate.instant('PASSENGER_CHANGE.MR');
  }

  navigateToPassengerChangeEditPage(): void {
    this.router.navigate([UrlPathnameEnum.PassengerChangeEdit]);
  }

  openFullscreenLoader(): void {
    this.store.dispatch(
      fullscreenLoaderActions.setVisibility({
        isVisible: true,
        loaderType: FullScreenLoaderEnum.MODAL,
        contentfulMessageKey: 'PASSENGER_LOADER.HEADER',
      })
    );
    this.paxChangeService
      .confirmPassengerUpdate(this.flightIdentifier)
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe(
        (response) => {
          this.store.dispatch(
            setSnackBarActions.setSnackBar({
              snackBarSuccess: true,
              showSnackBar: true,
              snackBarMessage: this.translate.instant('PASSENGER_CHANGE.RESULT.SUCCESS'),
            })
          );
          this.paxChangeService.setLastPassengerUpdateData(response);
          this.handleRedirectToOrderDetails();
        },
        (error) => {
          this.store.dispatch(setSnackBarActions.setSnackBar({ snackBarSuccess: false, showSnackBar: true, snackBarMessage: '' }));
          this.handleRedirectToOrderDetails();
        }
      );
  }

  finalize(): void {
    this.store.dispatch(fullscreenLoaderActions.setVisibility({ isVisible: false }));
    this.router.navigateByUrl(`${UrlPathnameEnum.CommonOrder}/${this.orderReference}`);
  }

  handleRedirectToOrderDetails() {
    this.store
      .select(selectErrorModal)
      .pipe(take(1))
      .subscribe((errorModal) => {
        if (errorModal.isVisible) {
          this.errorModalService.handleCloseEmitter.pipe(take(1), takeUntil(this.unsubscribe$)).subscribe(() => {
            this.finalize();
          });
        } else {
          this.finalize();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
